import React, { useState, useEffect } from 'react';
import {
  Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Toolbar, Typography, Divider, Avatar, Chip,
  useMediaQuery, AppBar, Button, Skeleton, BottomNavigation,
  BottomNavigationAction, Paper, LinearProgress, Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Dashboard as DashboardIcon,
  Payment as PaymentIcon,
  History,
  Receipt,
  Logout,
  AccountBalanceWallet,
  Warning,
  PeopleAlt,
  Apps,
} from '@mui/icons-material';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const DRAWER_WIDTH = 260;

interface WalletInfo {
  saldo: number;
  saldoMinimo: number;
  estado: string;
}

const fmt = (n: number) =>
  new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', maximumFractionDigits: 0 }).format(n);

const navItems = [
  { label: 'Dashboard', icon: <DashboardIcon />, path: '/agente/dashboard' },
  { label: 'Pagar', icon: <PaymentIcon />, path: '/agente/pagar' },
  { label: 'Transacções', icon: <History />, path: '/agente/transacoes' },
  { label: 'Comissões', icon: <Receipt />, path: '/agente/comissoes' },
  { label: 'Serviços', icon: <Apps />, path: '/agente/servicos' },
  { label: 'Clientes', icon: <PeopleAlt />, path: '/agente/clientes' },
];

const SidebarContent: React.FC<{ onClose?: () => void; wallet: WalletInfo | null; walletLoading: boolean }> = ({
  onClose,
  wallet,
  walletLoading,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleNav = (path: string) => {
    navigate(path);
    onClose?.();
  };

  const isLowBalance = wallet ? wallet.saldo < wallet.saldoMinimo : false;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.paper' }}>
      {/* Brand */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Typography
          variant="h5"
          fontWeight={800}
          color="white"
          letterSpacing="-0.5px"
          sx={{ fontFamily: '"Inter", sans-serif' }}
        >
          Pagali
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 500, letterSpacing: 1 }}>
          PORTAL DO AGENTE
        </Typography>
      </Box>

      {/* Agent info */}
      <Box sx={{ px: 2.5, py: 2, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: 'primary.main',
              fontSize: 16,
              fontWeight: 700,
            }}
          >
            {user?.name?.[0]?.toUpperCase() || 'A'}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" fontWeight={700} noWrap sx={{ lineHeight: 1.2 }}>
              {user?.name || 'Agente'}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {(user as any)?.agenteCodigo || user?.username || '—'}
            </Typography>
          </Box>
          <Chip label="Agente" size="small" color="primary" sx={{ height: 20, fontSize: 10, fontWeight: 600 }} />
        </Box>
      </Box>

      {/* Wallet balance */}
      <Box
        sx={{
          mx: 2,
          my: 1.5,
          p: 2,
          borderRadius: 2,
          bgcolor: isLowBalance ? 'error.50' : 'primary.50',
          border: '1px solid',
          borderColor: isLowBalance ? 'error.200' : 'primary.200',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <AccountBalanceWallet
            sx={{ fontSize: 16, color: isLowBalance ? 'error.main' : 'primary.main' }}
          />
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ letterSpacing: 0.5 }}>
            SALDO FLOAT
          </Typography>
          {isLowBalance && (
            <Tooltip title="Saldo abaixo do mínimo">
              <Warning sx={{ fontSize: 14, color: 'error.main', ml: 'auto' }} />
            </Tooltip>
          )}
        </Box>
        {walletLoading ? (
          <Skeleton variant="text" width={120} height={32} />
        ) : (
          <Typography
            variant="h6"
            fontWeight={800}
            color={isLowBalance ? 'error.main' : 'primary.main'}
            sx={{ lineHeight: 1 }}
          >
            {wallet ? fmt(wallet.saldo) : '—'}
          </Typography>
        )}
        {wallet && wallet.saldoMinimo > 0 && (
          <>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, (wallet.saldo / (wallet.saldoMinimo * 3)) * 100)}
              color={isLowBalance ? 'error' : 'primary'}
              sx={{ mt: 1, mb: 0.5, borderRadius: 4, height: 4 }}
            />
            <Typography variant="caption" color="text.secondary">
              Mínimo: {fmt(wallet.saldoMinimo)}
            </Typography>
          </>
        )}
      </Box>

      {/* Primary CTA */}
      <Box sx={{ px: 2, pb: 1.5 }}>
        <Button
          variant="contained"
          fullWidth
          startIcon={<PaymentIcon />}
          onClick={() => handleNav('/agente/pagar')}
          sx={{
            borderRadius: 2,
            py: 1.2,
            fontWeight: 700,
            fontSize: 14,
            boxShadow: '0 2px 8px rgba(21,101,192,0.35)',
            '&:hover': { boxShadow: '0 4px 12px rgba(21,101,192,0.45)' },
          }}
        >
          Processar Pagamento
        </Button>
      </Box>

      <Divider />

      {/* Navigation */}
      <List sx={{ flex: 1, py: 1, px: 1 }}>
        {navItems.map(item => {
          const active = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.25 }}>
              <ListItemButton
                onClick={() => handleNav(item.path)}
                sx={{
                  borderRadius: 1.5,
                  pl: active ? 1.5 : 2,
                  borderLeft: active ? '3px solid' : '3px solid transparent',
                  borderColor: active ? 'primary.main' : 'transparent',
                  bgcolor: active ? 'primary.50' : 'transparent',
                  '&:hover': { bgcolor: active ? 'primary.50' : 'action.hover' },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 36,
                    color: active ? 'primary.main' : 'text.secondary',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: active ? 700 : 400,
                    color: active ? 'primary.main' : 'text.primary',
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider />

      {/* Logout */}
      <List sx={{ py: 1, px: 1 }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={logout}
            sx={{ borderRadius: 1.5, '&:hover': { bgcolor: 'error.50', color: 'error.main' } }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
              <Logout fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Sair" primaryTypographyProps={{ fontSize: 14 }} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );
};

const AgentLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();

  const [wallet, setWallet] = useState<WalletInfo | null>(null);
  const [walletLoading, setWalletLoading] = useState(true);

  useEffect(() => {
    api
      .get('/agente/wallet')
      .then(res => setWallet(res.data?.data?.wallet || null))
      .catch(() => setWallet(null))
      .finally(() => setWalletLoading(false));
  }, []);

  // Determine active bottom nav value
  const bottomNavValue = navItems.findIndex(
    item => location.pathname === item.path || location.pathname.startsWith(item.path + '/'),
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {isMobile ? (
        /* ─── Mobile: top AppBar + bottom navigation ─── */
        <>
          <AppBar
            position="fixed"
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
              borderBottom: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <Toolbar sx={{ minHeight: 56, justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h6" fontWeight={800} lineHeight={1} letterSpacing="-0.5px">
                  Pagali
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1 }}>
                  Portal do Agente
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {walletLoading ? (
                  <Skeleton variant="rounded" width={90} height={28} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                ) : wallet ? (
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.15)',
                      border: '1px solid rgba(255,255,255,0.2)',
                    }}
                  >
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', display: 'block', lineHeight: 1 }}>
                      Float
                    </Typography>
                    <Typography variant="body2" fontWeight={700} lineHeight={1.2}>
                      {fmt(wallet.saldo)}
                    </Typography>
                  </Box>
                ) : null}
              </Box>
            </Toolbar>
          </AppBar>

          {/* Main content */}
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              bgcolor: 'action.hover',
              pt: '56px',
              pb: '56px',
              minHeight: '100vh',
              width: '100%',
            }}
          >
            <Outlet />
          </Box>

          {/* Bottom navigation */}
          <Paper
            elevation={8}
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: theme.zIndex.appBar,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <BottomNavigation
              value={bottomNavValue === -1 ? false : bottomNavValue}
              onChange={(_: unknown, newValue: number) => navigate(navItems[newValue].path)}
              sx={{ height: 56 }}
            >
              {navItems.map(item => (
                <BottomNavigationAction
                  key={item.path}
                  label={item.label}
                  icon={item.icon}
                  sx={{
                    fontSize: 10,
                    '&.Mui-selected': { color: 'primary.main' },
                    '& .MuiBottomNavigationAction-label': { fontSize: '10px !important' },
                  }}
                />
              ))}
            </BottomNavigation>
          </Paper>
        </>
      ) : (
        /* ─── Desktop: permanent sidebar ─── */
        <>
          <Drawer
            variant="permanent"
            sx={{
              width: DRAWER_WIDTH,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: DRAWER_WIDTH,
                boxSizing: 'border-box',
                borderRight: '1px solid',
                borderColor: 'divider',
              },
            }}
          >
            <SidebarContent wallet={wallet} walletLoading={walletLoading} />
          </Drawer>

          <Box
            component="main"
            sx={{
              flexGrow: 1,
              minHeight: '100vh',
              bgcolor: 'action.hover',
              ml: 0,
            }}
          >
            <Outlet />
          </Box>
        </>
      )}
    </Box>
  );
};

export default AgentLayout;
